import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';

import { useParams, useQuery } from '../../../contexts/RouterContext';
import { useRejectSnippets, useUnrejectSnippets } from '../../../hooks/api/snippets';
import { getProjectBaseURLFromParams } from '../../../utils';
import { NavigationButton } from '../../generic';
import DropdownButton from '../../generic/DropdownButton';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../generic/Pagination/consts';
import PaginationFooter from '../../generic/Pagination/PaginationFooter';
import { TreeViewLayout } from '../shared/TreeViewLayout';
import type { SnippetsQueryParams } from '../../../../shared/snippets';
import type { RevisionRouteParams } from '../projectDashboardTypes';

import useSyncSnippetDrawerWithQueryParams from './Drawer/useSyncSnippetDrawerWithQueryParams';
import GenericSnippetError from './emptyStates/GenericSnippetError';
import MissingSnippetAnalysis from './emptyStates/MissingSnippetAnalysis';
import UnsupportedSnippetProjectType from './emptyStates/UnsupportedSnippetProjectType';
import { useComparedSnippetsInfo, useSnippetsByUrl, useSnippetSelectionState } from './hooks';
import useSnippetGrouping from './hooks/useSnippetGrouping';
import useSnippetRootPathsByUrl from './hooks/useSnippetRootPathsByUrl';
import useSnippetsOrSnippetPackagesByUrl from './hooks/useSnippetsOrSnippetPackagesByUrl';
import PackageLabelsFilter from './PackageLabelsFilter';
import RejectionStatusFilter from './RejectionStatusFilter';
import SearchBar from './SearchBar';
import SnippetGroupingDropdown from './SnippetGroupingDropdown';
import SnippetsTable from './Table';
import SnippetsTreeView from './TreeView';
import { summarizePath, summarizeSnippetCount } from './utils';

import styles from './Snippets.module.less';

/**
 * Detect to see if we need to display a fallback image indicating
 * that this project type does not support snippet analysis.
 */
const isUnsupportedSnippetProjectTypeError = (err: Error | null): boolean => {
  const errorName = _.get(err, 'data.name');
  return errorName === 'UnsupportedSnippetProjectTypeError';
};

/**
 * Detect to see if we need to display a fallback image indicating that this
 * project supports snippet analysis but it hasn't been performed yet.
 */
const isMissingSnippetAnalysisError = (err: Error | null): boolean => {
  const errorName = _.get(err, 'data.name');
  return errorName === 'MissingSnippetAnalysisError';
};

/**
 * SnippetsContainer - Route container component for the Snippet Scanning page within a project.
 */
const SnippetsContainer: VoidFunctionComponent = () => {
  //  Note: this will change (and probably be moved) but for now this pulls the dummy data so we can see it.
  const params = useParams<RevisionRouteParams>();
  const userRevisionLocator = `${params.project_id}$${params.revision_id}`;

  const { data: rootPaths } = useSnippetRootPathsByUrl();
  const rootPath = rootPaths?.paths[0] ?? {
    type: 'directory',
    name: '...',
    path: '/',
    count: 0,
  };

  const comparedSnippetsInfo = useComparedSnippetsInfo();
  const shouldShowCompareButton = !comparedSnippetsInfo.isComparing;

  // If we are comparing snippets and the status is "removed", then we need to [un]reject
  // snippets based on the older revision because we fetch them from Sparkle based on a
  // specific user revision.
  const isComparingRemovedSnippets = comparedSnippetsInfo.isComparing && comparedSnippetsInfo.status === 'removed';
  const relevantUserRevisionLocator = isComparingRemovedSnippets
    ? comparedSnippetsInfo.olderRevisionLocator
    : userRevisionLocator;

  const [queryParams, setQuery] = useQuery<SnippetsQueryParams>();
  const { data, error } = useSnippetsOrSnippetPackagesByUrl();

  // Always load snippets to power the "N snippets found" text.
  const { data: snippets } = useSnippetsByUrl();

  const { snippetGrouping, setSnippetGrouping } = useSnippetGrouping();
  // If snippet grouping is not enabled, we always treat the grouping as "ungrouped".
  const selectionState = useSnippetSelectionState(data?.results ?? [], snippetGrouping);

  // Sync the snippet drawer with query params using Dresser
  useSyncSnippetDrawerWithQueryParams(userRevisionLocator);

  // Ensure we invalidate the current revision's snippets list since
  // [un]rejections target the older revision when comparing removed snippets.
  const { mutate: rejectSnippets } = useRejectSnippets(
    relevantUserRevisionLocator,
    { revisionLocator: userRevisionLocator },
  );
  const rejectMatches = () => {
    if (selectionState.selection === 'all') {
      rejectSnippets(queryParams);
    } else if (snippetGrouping === 'ungrouped') {
      rejectSnippets({
        path: queryParams.path,
        ids: Array.from(selectionState.selection.snippets),
      });
    } else if (snippetGrouping === 'package') {
      const ids: string[] = [];
      const packageIds: string[] = [];
      // Gather all selected snippet and package IDs across the current selection.
      selectionState.selection.packages.forEach((packageSelection, packageId) => {
        if (packageSelection.snippets === 'all') {
          packageIds.push(packageId);
        } else {
          packageSelection.snippets.forEach((snippetId) => {
            ids.push(snippetId);
          });
        }
      });
      rejectSnippets({
        path: queryParams.path,
        ids,
        packageIds,
      });
    }

    // Clear the selection after rejecting.
    selectionState.clearSelection();
  };

  // Ensure we invalidate the current revision's snippets list since
  // [un]rejections target the older revision when comparing removed snippets.
  const { mutate: unrejectSnippets } = useUnrejectSnippets(
    relevantUserRevisionLocator,
    { revisionLocator: userRevisionLocator },
  );
  const unrejectMatches = () => {
    if (selectionState.selection === 'all') {
      unrejectSnippets(queryParams);
    } else if (snippetGrouping === 'ungrouped') {
      unrejectSnippets({
        path: queryParams.path,
        ids: Array.from(selectionState.selection.snippets),
      });
    } else if (snippetGrouping === 'package') {
      const ids: string[] = [];
      const packageIds: string[] = [];
      // Gather all selected snippet and package IDs across the current selection.
      selectionState.selection.packages.forEach((packageSelection, packageId) => {
        if (packageSelection.snippets === 'all') {
          packageIds.push(packageId);
        } else {
          packageSelection.snippets.forEach((snippetId) => {
            ids.push(snippetId);
          });
        }
      });
      unrejectSnippets({
        path: queryParams.path,
        ids,
        packageIds,
      });
    }

    // Clear the selection after unrejecting.
    selectionState.clearSelection();
  };

  if (error) {
    if (isUnsupportedSnippetProjectTypeError(error)) {
      return <UnsupportedSnippetProjectType />;
    }

    if (isMissingSnippetAnalysisError(error)) {
      return <MissingSnippetAnalysis />;
    }

    return <GenericSnippetError error={error} />;
  }

  const compareURL = `${getProjectBaseURLFromParams(params)}/browse/snippets/compare/new`;
  return (
    <>
      <header className={styles.heading}>
        <h1 className={styles.title}>
          Snippets
        </h1>
        {!!snippets?.totalCount && (
          <div className={styles.summary}>
            <b>{summarizeSnippetCount(snippets.totalCount)}</b> found in <b>{summarizePath(queryParams.path)}</b>
          </div>
        )}
      </header>
      <div className={styles.subheading}>
        <div className={styles.filters}>
          <div className={styles.search}>
            <SearchBar
              initialSearch={queryParams.search ?? ''}
              onSearchChange={(value) => {
                if (value) {
                  setQuery({ ...queryParams, search: value, path: '/', page: undefined });
                } else {
                  // If the search is cleared, we want to remove the search from the params.
                  setQuery({ ..._.omit(queryParams, 'search'), path: '/', page: undefined });
                }
                // Clear the selection when changing filters.
                selectionState.clearSelection();
              }}
            />
          </div>
          <div className={styles.rejectionStatus}>
            <RejectionStatusFilter
              initialValues={queryParams.rejectionStatus ?? []}
              onFilterChange={(values) => {
                if (values.length > 0) {
                  setQuery({ ...queryParams, rejectionStatus: values, path: '/', page: undefined });
                } else {
                  // If the rejection status filter is cleared, we want to remove it from the params.
                  setQuery({ ..._.omit(queryParams, 'rejectionStatus'), path: '/', page: undefined });
                }
                // Clear the selection when changing filters.
                selectionState.clearSelection();
              }}
            />
          </div>
          <div className={styles.packageLabels}>
            <PackageLabelsFilter
              initialValues={queryParams.packageLabels ?? []}
              onFilterChange={(values) => {
                if (values.length > 0) {
                  setQuery({ ...queryParams, packageLabels: values, path: '/', page: undefined });
                } else {
                  // If the package labels filter is cleared, we want to remove it from the params.
                  setQuery({ ..._.omit(queryParams, 'packageLabels'), path: '/', page: undefined });
                }
                // Clear the selection when changing filters.
                selectionState.clearSelection();
              }}
            />
          </div>
        </div>
        <div className={styles.actions}>
          <SnippetGroupingDropdown
            snippetGrouping={snippetGrouping}
            setSnippetGrouping={(grouping) => {
              setSnippetGrouping(grouping);
              // Clear the selection when changing how snippets are grouped.
              selectionState.clearSelection();
            }}
          />
          {shouldShowCompareButton && (
            <NavigationButton
              to={compareURL}
              size="medium"
              type="secondary"
            >
              Compare
            </NavigationButton>
          )}
          <DropdownButton
            id="snippets-actions-dropdown"
            title="Actions"
            size="medium"
            disabled={selectionState.isAnythingSelected() === false}
            pullRight
          >
            <DropdownButton.Item onClick={rejectMatches}>
              Reject matches
            </DropdownButton.Item>
            <DropdownButton.Item onClick={unrejectMatches}>
              Stop rejecting
            </DropdownButton.Item>
          </DropdownButton>
        </div>
      </div>
      <TreeViewLayout>
        <SnippetsTreeView
          rootPath={rootPath}
          userRevisionLocator={userRevisionLocator}
          selectedPath={queryParams.path ?? '/'}
          setSelectedPath={(path) => {
            setQuery({ ...queryParams, path });

            // Clear the selection after changing pages.
            selectionState.clearSelection();
          }}
        />
        <SnippetsTable
          userRevisionLocator={userRevisionLocator}
          selectionState={selectionState}
        />
      </TreeViewLayout>
      <PaginationFooter
        page={Number(queryParams.page) || DEFAULT_PAGE}
        count={data?.pageSize ?? DEFAULT_PAGE_SIZE}
        totalItems={data?.totalCount ?? 0}
        setPage={((page) => {
          setQuery({ ...queryParams, page });

          // Clear the selection after changing pages.
          selectionState.clearSelection();
        })}
        setCount={((pageSize) => {
          setQuery({
            ...queryParams,
            pageSize,
            //  Unset the page param (it should default to 1).
            page: undefined,
          });

          // Clear the selection after changing the page size.
          selectionState.clearSelection();
        })}
      />
    </>
  );
};

export default SnippetsContainer;
